import { FormEvent, useEffect, useState } from "react";
import {
  createReview,
  getProductReviews,
  ReviewSummary,
} from "../../../Services/review.service.ts";

const Stars = ({
  value,
  onPick,
}: {
  value: number;
  onPick?: (rating: number) => void;
}) => (
  <span
    className="stars"
    role={onPick ? "radiogroup" : "img"}
    aria-label={`${value} out of 5 stars`}
  >
    {[1, 2, 3, 4, 5].map((star) =>
      onPick ? (
        <button
          key={star}
          type="button"
          className={star <= value ? "on" : ""}
          aria-label={`${star} star${star > 1 ? "s" : ""}`}
          onClick={() => onPick(star)}
        >
          ★
        </button>
      ) : (
        <i key={star} className={star <= value ? "on" : ""}>
          ★
        </i>
      ),
    )}
  </span>
);

const formatWhen = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const ProductReviews = ({
  productId,
  onSummary,
}: {
  productId: string;
  onSummary: (summary: { average: number; count: number }) => void;
}) => {
  const [summary, setSummary] = useState<ReviewSummary>({
    average: 0,
    count: 0,
    reviews: [],
  });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sending, setSending] = useState(false);
  const [writing, setWriting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let active = true;
    getProductReviews(productId)
      .then((data) => {
        if (!active) return;
        setSummary(data);
        onSummary({ average: data.average, count: data.count });
      })
      .catch(() => {
        if (!active) return;
        onSummary({ average: 0, count: 0 });
      });
    return () => {
      active = false;
    };
  }, [productId, onSummary]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (rating < 1) {
      setError("Choose a star rating.");
      return;
    }
    if (name.trim().length < 2) {
      setError("Enter your name.");
      return;
    }
    if (comment.trim().length < 2) {
      setError("Write a short comment.");
      return;
    }

    setSending(true);
    try {
      const data = await createReview(productId, {
        name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      setSummary(data);
      onSummary({ average: data.average, count: data.count });
      setName("");
      setRating(0);
      setComment("");
      setWriting(false);
      setNotice("Thank you. Your review is live.");
    } catch (submitError: any) {
      setError(submitError.message || "Could not post your review.");
    } finally {
      setSending(false);
    }
  };

  const visible = showAll ? summary.reviews : summary.reviews.slice(0, 3);

  return (
    <section className="reviews" id="reviews">
      <div className="reviewHead">
        <h2>Reviews</h2>
        <span>
          {summary.count === 0
            ? "No reviews yet"
            : `${summary.average.toFixed(1)} · ${summary.count}`}
        </span>
        <button type="button" onClick={() => setWriting((open) => !open)}>
          {writing ? "Close" : "Write a review"}
        </button>
      </div>
      {notice && <p className="reviewNotice">{notice}</p>}
      {writing && (
        <form className="reviewForm" onSubmit={submit}>
          <input
            value={name}
            maxLength={40}
            aria-label="Your name"
            onChange={(event) => setName(event.target.value)}
            placeholder="Your name"
          />
          <Stars value={rating} onPick={setRating} />
          <input
            value={comment}
            maxLength={500}
            aria-label="Comment"
            onChange={(event) => setComment(event.target.value)}
            placeholder="Your comment"
          />
          <button className="cta dark" type="submit" disabled={sending}>
            {sending ? "Posting..." : "Post"}
          </button>
          {error && <p className="reviewError">{error}</p>}
        </form>
      )}
      {visible.map((review) => (
        <article key={review.id} className="review">
          <div>
            <b>{review.name}</b>
            <time dateTime={review.createdAt}>{formatWhen(review.createdAt)}</time>
          </div>
          <Stars value={review.rating} />
          <p>{review.comment}</p>
        </article>
      ))}
      {summary.reviews.length > 3 && (
        <button
          type="button"
          className="reviewMore"
          onClick={() => setShowAll((open) => !open)}
        >
          {showAll ? "Show less" : `Show all ${summary.reviews.length}`}
        </button>
      )}
    </section>
  );
};

export default ProductReviews;
